/** Opt-in presentation clock. No gameplay delays, audio selection, or targeting state. */
import { validMagicArea } from './magic-area';
export const SPELL_RELEASE_MS = 1000;
/** Without a gesture the effect still needs the cue's trip into the scene and its textures: a short lead,
 *  not zero (a started-in-the-past dart pops in mid-flight). */
export const NO_GESTURE_RELEASE_MS = 250;
export type SpellPresentationTiming = { gestureAt: number; releaseAt: number; soundAt?: number };
/** Per-recipe visual offset from the shared gesture/SFX cue; silence inside a
 * sound take is not evidence that the visual should start with the media file. */
export function validSpellVfxDelay(recipe: any): boolean {
  return recipe?.vfxDelayMs === undefined || (recipe?.castTiming === 'gesture-release-v1'
    && Number.isInteger(recipe.vfxDelayMs) && recipe.vfxDelayMs >= 0 && recipe.vfxDelayMs <= 2000);
}
// ONE map per page (window-scoped): the parent's bundles (VFX feedback, damage feedback, the sound mixer) each embed
// this module, and every one of them must resolve an action to the SAME clock.
const clocks: Map<string, SpellPresentationTiming> = ((globalThis as { __ftSpellCastClocks?: Map<string, SpellPresentationTiming> }).__ftSpellCastClocks ??= new Map());
export function spellPresentationTiming(cue: any, now = Date.now()): SpellPresentationTiming | null {
  if (cue?.recipe?.castTiming !== 'gesture-release-v1' || !validSpellVfxDelay(cue.recipe) || !cue.id
    || !cue.sourceEntityId && !validMagicArea(cue.area)) return null;
  // One clock per action: a cue and its rows (possibly on different channels) must agree on the key's case.
  const key = [cue.encounterId || '', cue.sourceEntityId, String(cue.id).split(':')[0]].map(part => String(part).toUpperCase()).join(':');
  let clock = clocks.get(key);
  if (!clock) {
    // No gesture to wait for (a standee, a caster this viewer cannot see, a dead rig): the FT3D scene says so
    // through ftCastGestureAvailable and the release comes after NO_GESTURE_RELEASE_MS instead of a buildup
    // nobody can watch. Absent hook (no scene yet, other surfaces) keeps the cast window.
    let gesture = !!cue.sourceEntityId;
    try { if (gesture) gesture = (globalThis as { ftCastGestureAvailable?: (id: string) => boolean }).ftCastGestureAvailable?.(String(cue.sourceEntityId)) !== false; } catch { /* detached scene */ }
    const gestureAt = gesture ? now : now + NO_GESTURE_RELEASE_MS - SPELL_RELEASE_MS;
    clock = { gestureAt, releaseAt: gestureAt + SPELL_RELEASE_MS };
    if(cue.recipe.vfxDelayMs){clock.soundAt=clock.releaseAt;clock.releaseAt+=cue.recipe.vfxDelayMs;}
    clocks.set(key, clock);
    if (clocks.size > 400) clocks.delete(clocks.keys().next().value!);
  }
  // Area onset and recipient contact share a gesture, but each complete recipe retains its visual delay.
  const delay = cue.recipe.vfxDelayMs ?? 0;
  if (clock.releaseAt - clock.gestureAt !== SPELL_RELEASE_MS + delay) return {
    gestureAt: clock.gestureAt, releaseAt: clock.gestureAt + SPELL_RELEASE_MS + delay,
    ...(delay ? {soundAt: clock.gestureAt + SPELL_RELEASE_MS} : {}) };
  return clock;
}
/** Where in a projectile recipe's duration its flight LANDS (the impact flash) — the VFX runtime draws the plain
 *  darts and the purchased bolt/fire flipbooks on this fraction, the gas puff on its own. One source for both. */
export const PROJECTILE_IMPACT_FRACTION = 0.48;
export const GAS_PUFF_IMPACT_FRACTION = 0.55;
/** Ms after the VFX start at which a projectile-shaped cue reaches its target; null for every other shape
 *  (area, self, contact, beam…) and for the wind-push gust, which rides its displacement, not a flight. */
export function projectileImpactMs(cue: any): number | null {
  const r = cue?.recipe;
  // An attack-sourced cue lands on the swing impact, never the cast clock (FT3D_CAST_SYNC_AUDIT.md) — defensive.
  if (r?.kind !== 'projectile' || r.projectileStyle === 'purchased-wind-push' || cue?.sourceKind === 'attack') return null;
  // Same test the VFX runtime uses (`impactOffsetMs !== undefined`), so the flash and the hit can never disagree.
  if (cue.impactOffsetMs !== undefined && Number.isFinite(Number(cue.impactOffsetMs))) return Math.max(0, Number(cue.impactOffsetMs));
  if (!Number.isFinite(r.duration) || r.duration <= 0) return null;
  return Math.round(r.duration * 1000 * (r.projectileStyle === 'purchased-gas-puff' ? GAS_PUFF_IMPACT_FRACTION : PROJECTILE_IMPACT_FRACTION));
}
/**
 * When a cast's SFX HIT is heard: on the release, except a projectile's, which lands when the projectile ARRIVES
 * (release + its flight — the moment the VFX shows the impact). A vfxDelayMs clock keeps its tuned take-start
 * moment (`soundAt`) untouched.
 */
export function castSoundAt(timing: SpellPresentationTiming, cue: any): number {
  if (timing.soundAt !== undefined) return timing.soundAt;
  return timing.releaseAt + (projectileImpactMs(cue) ?? 0);
}
/**
 * The cast clock a roll row times its SFX on: its own cue, else the server's `roll_cast_clock` marker for a
 * recipe whose visual arrives on another channel (an area's magic_area envelope, a status landing) — keyed
 * to the same action, so whichever of the two lands first starts the one clock.
 */
export function rollPresentationTiming(roll: any, now = Date.now()): SpellPresentationTiming | null {
  if (roll?.roll_vfx) return spellPresentationTiming(roll.roll_vfx, now);
  const marker = roll?.roll_cast_clock;
  if (!marker || marker.castTiming !== 'gesture-release-v1' || !roll.roll_id || !roll.roll_by) return null;
  return spellPresentationTiming({ id: `${roll.roll_presentation?.actionId || roll.roll_id}:clock`, sourceEntityId: roll.roll_by, encounterId: roll.encounter_id,
    recipe: { castTiming: marker.castTiming, ...(marker.vfxDelayMs !== undefined ? { vfxDelayMs: marker.vfxDelayMs } : {}) } }, now);
}
